/** Owner console (/owner): set up agencies and their first administrators; see every agency at a glance. */
import { Fragment, useEffect, useState } from 'react';
import { AuthenticatorKey } from '../shared/AuthenticatorKey.tsx';
import { api, ApiError } from '../shared/api.ts';
import { AuthShell } from '../shared/login.tsx';
import { Badge, Card, Empty, ErrorBox, Field, Notice, Spinner, useAction, useLoad } from '../shared/ui.tsx';

type Me = { email: string; displayName: string; totpEnabled: boolean; mfaVerified: boolean; emailMode: 'resend' | 'postmark' | 'log' };

const when = (v: string | null) => (v ? new Date(v).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');
const day = (v: string | null) => (v ? new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

function LinkCopy({ label, link }: { label: string; link: string }) {
  const url = `${location.origin}${link}`;
  const [copied, setCopied] = useState(false);
  return (
    <div className="link-box">
      <div className="small">{label}</div>
      <div className="row gap-sm wrap">
        <input readOnly value={url} className="grow mono" aria-label="Invitation link" onFocus={(e) => e.target.select()} />
        <button type="button" className="btn sm" onClick={() => navigator.clipboard?.writeText(url).then(() => setCopied(true))}>
          {copied ? '✓ Copied' : 'Copy link'}
        </button>
      </div>
    </div>
  );
}

function EmailModeNotice({ mode }: { mode: Me['emailMode'] }) {
  if (mode !== 'log') return null;
  return (
    <Notice tone="warn">
      <strong>Email isn’t connected yet.</strong> Invitations and password-reset emails are saved under <em>Email log</em> instead of being sent — copy the link from there and pass it on. To send for real, add <code>EMAIL_FROM</code> and <code>RESEND_API_KEY</code> (or <code>POSTMARK_SERVER_TOKEN</code>) in Railway, plus <code>PUBLIC_URL</code>.
    </Notice>
  );
}

function OwnerLogin({ onIn }: { onIn: () => void }) {
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const a = useAction();
  return (
    <AuthShell subtitle="Owner console">
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          a.run(async () => {
            await api.post('/api/owner/login', { email, password: pw });
            onIn();
          });
        }}
      >
        <Field label="Email">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" required />
        </Field>
        <Field label="Password">
          <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="current-password" required />
        </Field>
        <ErrorBox error={a.error} />
        <button className="btn primary" disabled={a.busy}>
          Sign in
        </button>
        <p className="small muted">For the person who runs CivilHound. Agency staff sign in at <a href="/signin">/signin</a>.</p>
      </form>
    </AuthShell>
  );
}

function OwnerMfa({ me, onIn, onOut }: { me: Me; onIn: () => void; onOut: () => void }) {
  const [enroll, setEnroll] = useState<{ secret: string; otpauthUri: string } | null>(null);
  const [code, setCode] = useState('');
  const a = useAction();
  return (
    <AuthShell subtitle="Owner console · two-factor">
      <div className="stack">
        {!me.totpEnabled && !enroll && (
          <>
            <Notice>The owner console always needs an authenticator app. Set one up to continue.</Notice>
            <button className="btn primary" onClick={() => a.run(async () => setEnroll(await api.post('/api/owner/mfa/enroll')))}>
              Set up authenticator
            </button>
          </>
        )}
        {enroll && (
          <AuthenticatorKey secret={enroll.secret} otpauthUri={enroll.otpauthUri} />
        )}
        {(me.totpEnabled || enroll) && (
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault();
              a.run(async () => {
                await api.post('/api/owner/mfa/verify', { code });
                onIn();
              });
            }}
          >
            <Field label="6-digit code">
              <input inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} required autoFocus />
            </Field>
            <button className="btn primary" disabled={a.busy || code.length !== 6}>
              Verify
            </button>
          </form>
        )}
        <ErrorBox error={a.error} />
        <button className="btn link" onClick={onOut}>
          Sign out
        </button>
      </div>
    </AuthShell>
  );
}

const TZ = [
  ['America/New_York', 'Eastern'],
  ['America/Chicago', 'Central'],
  ['America/Denver', 'Mountain'],
  ['America/Phoenix', 'Arizona'],
  ['America/Los_Angeles', 'Pacific'],
  ['America/Anchorage', 'Alaska'],
  ['Pacific/Honolulu', 'Hawaii'],
];

function NewAgency({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const [f, setF] = useState({ product: 'bluehq', code: '', name: '', countyName: '', timezone: 'America/New_York', contactPhone: '', contactAddress: '', adminName: '', adminEmail: '', adminTitle: '' });
  const [made, setMade] = useState<{ code: string; inviteLink: string; email: string } | null>(null);
  const a = useAction();
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  if (made)
    return (
      <Card title={`${made.code} is set up`}>
        <Notice tone="ok">
          The agency is ready with the standard South Carolina rules, affidavit templates and its three county courts. An invitation went to <strong>{made.email}</strong> to become its administrator.
        </Notice>
        <LinkCopy label="Their invitation link (works for 7 days) — send it yourself if email isn’t connected:" link={made.inviteLink} />
        <p className="small muted">Next, the administrator signs in, sets up two-factor, then invites deputies and clerks and checks the rules and fees under Administration.</p>
        <button className="btn primary" onClick={onDone}>
          Done
        </button>
      </Card>
    );
  return (
    <Card title="New agency">
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          a.run(async () => {
            const r = await api.post<{ code: string; inviteLink: string }>('/api/owner/agencies', {
              code: f.code,
              name: f.name,
              product: f.product,
              countyName: f.countyName,
              timezone: f.timezone,
              contactPhone: f.contactPhone || undefined,
              contactAddress: f.contactAddress || undefined,
              admin: { displayName: f.adminName, email: f.adminEmail, title: f.adminTitle || undefined },
            });
            setMade({ code: r.code, inviteLink: r.inviteLink, email: f.adminEmail });
          });
        }}
      >
        <h3 className="small-h">The agency</h3>
        <Field label="What they bought" hint="BlueHQ is everything, civil process included; they sign in at 803systems.com. CivilHound is civil process only; they sign in at civilprocesssoftware.com.">
          <select value={f.product} onChange={set('product')} aria-label="Product">
            <option value="bluehq">BlueHQ (records management, civil process included)</option>
            <option value="civilhound">CivilHound (civil process only)</option>
          </select>
        </Field>
        <div className="grid2">
          <Field label="Agency name">
            <input value={f.name} onChange={set('name')} required placeholder="Chester County Sheriff’s Office" aria-label="Agency name" />
          </Field>
          <Field label="Agency code" hint="Short; staff type it at sign-in. 2–12 letters/numbers.">
            <input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })} required maxLength={12} placeholder="CHCO" className="mono" aria-label="Agency code" />
          </Field>
          <Field label="County">
            <input value={f.countyName} onChange={set('countyName')} required placeholder="Chester County, SC" aria-label="County" />
          </Field>
          <Field label="Time zone">
            <select value={f.timezone} onChange={set('timezone')} aria-label="Time zone">
              {TZ.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Office phone (optional)">
            <input value={f.contactPhone} onChange={set('contactPhone')} aria-label="Office phone" />
          </Field>
          <Field label="Office address (optional)">
            <input value={f.contactAddress} onChange={set('contactAddress')} aria-label="Office address" />
          </Field>
        </div>
        <h3 className="small-h">Its first administrator</h3>
        <div className="grid2">
          <Field label="Name">
            <input value={f.adminName} onChange={set('adminName')} required aria-label="Administrator name" />
          </Field>
          <Field label="Email">
            <input type="email" value={f.adminEmail} onChange={set('adminEmail')} required aria-label="Administrator email" />
          </Field>
          <Field label="Title (optional)">
            <input value={f.adminTitle} onChange={set('adminTitle')} placeholder="e.g. Civil Process Lieutenant" aria-label="Administrator title" />
          </Field>
        </div>
        <ErrorBox error={a.error} />
        <div className="row gap">
          <button className="btn primary" disabled={a.busy}>
            {a.busy ? 'Setting up…' : 'Create agency & invite administrator'}
          </button>
          <button type="button" className="btn ghost" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
    </Card>
  );
}

function AgencyDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const r = useLoad(() => api.get<any>(`/api/owner/agencies/${id}`), [id]);
  const a = useAction();
  const [inv, setInv] = useState({ displayName: '', email: '' });
  const [link, setLink] = useState<string | null>(null);
  if (!r.data) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const ag = r.data.agency;
  return (
    <div className="stack">
      <button className="btn link" onClick={onBack}>
        ← All agencies
      </button>
      <div className="row between wrap gap">
        <div>
          <h1>
            {ag.name} <span className="mono muted">{ag.code}</span>
          </h1>
          <div className="muted small">
            {ag.county ?? '—'} · {ag.timezone} · created {day(ag.created_at)}
            {ag.contactPhone ? ` · ${ag.contactPhone}` : ''}
          </div>
        </div>
        {ag.suspended_at ? (
          <div className="row gap-sm">
            <Badge tone="bad">Suspended {day(ag.suspended_at)}</Badge>
            <button className="btn" disabled={a.busy} onClick={() => a.run(async () => (await api.post(`/api/owner/agencies/${id}/reactivate`), r.reload()))}>
              Reactivate
            </button>
          </div>
        ) : (
          <button
            className="btn warn"
            disabled={a.busy}
            onClick={() => {
              const reason = window.prompt(`Suspend ${ag.code}? Everyone in it is signed out and can’t sign in until you reactivate. Reason:`)?.trim();
              if (reason) a.run(async () => (await api.post(`/api/owner/agencies/${id}/suspend`, { reason }), r.reload()));
            }}
          >
            Suspend agency
          </button>
        )}
      </div>
      <ErrorBox error={a.error} />
      <Card title="Product">
        <div className="row gap wrap" style={{ alignItems: 'center' }}>
          <select
            aria-label="Product"
            value={ag.product}
            disabled={a.busy}
            onChange={(e) => {
              const product = e.target.value;
              const label = product === 'bluehq' ? 'BlueHQ' : 'CivilHound';
              const site = product === 'bluehq' ? '803systems.com' : 'civilprocesssoftware.com';
              if (window.confirm(`Switch ${ag.code} to ${label}? From now on its people sign in at ${site}.${product === 'civilhound' ? ' Reports, evidence, warrants, court, messages and fleet are hidden (nothing is deleted).' : ''}`))
                a.run(async () => (await api.post(`/api/owner/agencies/${id}/product`, { product }), r.reload()));
            }}
          >
            <option value="bluehq">BlueHQ: everything, civil process included</option>
            <option value="civilhound">CivilHound: civil process only</option>
          </select>
          <span className="small muted">Signs in at {ag.product === 'bluehq' ? '803systems.com' : 'civilprocesssoftware.com'}</span>
        </div>
      </Card>
      <div className="tiles">
        {[
          [ag.users, 'active users'],
          [ag.active_users_7d, 'signed in this week'],
          [ag.open_papers, 'open papers'],
          [ag.papers_30d, 'papers in 30 days'],
        ].map(([n, l]) => (
          <div key={String(l)} className="tile">
            <div className="tile-n">{n}</div>
            <div className="tile-l">{l}</div>
          </div>
        ))}
      </div>
      <Card title="Users">
        {r.data.users.length === 0 ? (
          <Empty>No one has accepted an invitation yet.</Empty>
        ) : (
          <table className="table small">
            <thead>
              <tr>
                <th>Name</th>
                <th>Username</th>
                <th>Role</th>
                <th>2FA</th>
                <th>Last seen</th>
              </tr>
            </thead>
            <tbody>
              {r.data.users.map((u: any) => (
                <tr key={u.id} className={u.active ? '' : 'muted'}>
                  <td>{u.display_name}</td>
                  <td className="mono">{u.username}</td>
                  <td>{u.roleLabel}</td>
                  <td>{u.totp_enabled ? <Badge tone="ok">on</Badge> : <Badge>off</Badge>}</td>
                  <td>{when(u.last_seen)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
      <Card title="Invitations waiting">
        {r.data.invites.length === 0 ? (
          <Empty>None.</Empty>
        ) : (
          <ul className="plain">
            {r.data.invites.map((i: any) => (
              <li key={i.id}>
                {i.display_name} · {i.email} · {i.role === 'agency_admin' ? 'Administrator' : i.role} · sent {day(i.created_at)} {i.expired && <Badge tone="bad">expired</Badge>}
              </li>
            ))}
          </ul>
        )}
        <form
          className="row gap wrap align-end"
          onSubmit={(e) => {
            e.preventDefault();
            a.run(async () => {
              const x = await api.post<{ inviteLink: string }>(`/api/owner/agencies/${id}/invite-admin`, inv);
              setLink(x.inviteLink);
              setInv({ displayName: '', email: '' });
              r.reload();
            });
          }}
        >
          <Field label="Invite another administrator — name">
            <input value={inv.displayName} onChange={(e) => setInv({ ...inv, displayName: e.target.value })} required />
          </Field>
          <Field label="Email">
            <input type="email" value={inv.email} onChange={(e) => setInv({ ...inv, email: e.target.value })} required />
          </Field>
          <button className="btn">Send invitation</button>
        </form>
        {link && <LinkCopy label="Invitation link (works for 7 days):" link={link} />}
      </Card>
    </div>
  );
}

function Agencies({ me }: { me: Me }) {
  const r = useLoad(() => api.get<any>('/api/owner/agencies'));
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  if (open) return <AgencyDetail id={open} onBack={() => (setOpen(null), r.reload())} />;
  return (
    <div className="stack">
      <EmailModeNotice mode={me.emailMode} />
      {adding ? (
        <NewAgency onDone={() => (setAdding(false), r.reload())} onCancel={() => setAdding(false)} />
      ) : (
        <div className="row between wrap gap">
          <h1>Agencies</h1>
          <button className="btn primary" onClick={() => setAdding(true)}>
            + New agency
          </button>
        </div>
      )}
      {!r.data ? (
        r.error ? <ErrorBox error={r.error} /> : <Spinner />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Agency</th>
                <th>County</th>
                <th>Users</th>
                <th>Active this week</th>
                <th>Open papers</th>
                <th>Papers (30 days)</th>
                <th>Last activity</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {r.data.agencies.map((x: any) => (
                <tr key={x.id}>
                  <td>
                    <strong>{x.name}</strong> <span className="mono muted small">{x.code}</span>
                    {x.suspended_at && (
                      <>
                        {' '}
                        <Badge tone="bad">suspended</Badge>
                      </>
                    )}
                    {x.pending_invites > 0 && <div className="small muted">{x.pending_invites} invitation(s) waiting</div>}
                  </td>
                  <td className="small">{x.county ?? '—'}</td>
                  <td>{x.users}</td>
                  <td>{x.active_users_7d}</td>
                  <td>{x.open_papers}</td>
                  <td>{x.papers_30d}</td>
                  <td className="small">{when(x.last_activity)}</td>
                  <td>
                    <button className="btn sm" onClick={() => setOpen(x.id)}>
                      Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="small muted">You see agencies and counts only — never papers, people or case details.</p>
    </div>
  );
}

function EmailLog({ me }: { me: Me }) {
  const r = useLoad(() => api.get<any>('/api/owner/emails'));
  const [show, setShow] = useState<string | null>(null);
  return (
    <div className="stack">
      <h1>Email log</h1>
      <EmailModeNotice mode={me.emailMode} />
      {!r.data ? (
        <Spinner />
      ) : r.data.emails.length === 0 ? (
        <Empty>No email yet.</Empty>
      ) : (
        <table className="table small">
          <thead>
            <tr>
              <th>When</th>
              <th>Agency</th>
              <th>To</th>
              <th>Subject</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {r.data.emails.map((e: any) => (
              <Fragment key={e.id}>
                <tr>
                  <td>{when(e.created_at)}</td>
                  <td className="mono">{e.agency_code ?? '—'}</td>
                  <td>{e.to_addr}</td>
                  <td>{e.subject}</td>
                  <td>
                    <Badge tone={e.status === 'sent' ? 'ok' : e.status === 'failed' ? 'bad' : 'warn'}>{e.status === 'logged' ? 'not sent (log only)' : e.status}</Badge>
                    {e.error && <div className="bad-text">{e.error}</div>}
                  </td>
                  <td>
                    <button className="btn sm ghost" onClick={() => setShow(show === e.id ? null : e.id)}>
                      {show === e.id ? 'Hide' : 'View'}
                    </button>
                  </td>
                </tr>
                {show === e.id && (
                  <tr>
                    <td colSpan={6}>
                      <pre className="email-body">{e.body_text}</pre>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Activity() {
  const r = useLoad(() => api.get<any>('/api/owner/activity'));
  return (
    <div className="stack">
      <h1>Activity</h1>
      {!r.data ? (
        <Spinner />
      ) : (
        <table className="table small">
          <thead>
            <tr>
              <th>When</th>
              <th>Who</th>
              <th>What</th>
              <th>Agency</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {r.data.events.map((e: any) => (
              <tr key={e.id}>
                <td>{when(e.at)}</td>
                <td>{e.owner}</td>
                <td className="mono">{e.action}</td>
                <td className="mono">{e.agency_code ?? ''}</td>
                <td className="muted mono wrap-any">{Object.keys(e.details ?? {}).length ? JSON.stringify(e.details) : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function OwnerApp() {
  const [me, setMe] = useState<Me | null | undefined>(undefined);
  const [tab, setTab] = useState<'agencies' | 'email' | 'activity'>('agencies');
  const load = () =>
    api
      .get<Me>('/api/owner/me')
      .then(setMe)
      .catch((e) => (e instanceof ApiError && e.status === 401 ? setMe(null) : setMe(null)));
  useEffect(() => void load(), []);
  const out = () => api.post('/api/owner/logout').finally(() => setMe(null));
  if (me === undefined)
    return (
      <div className="center">
        <Spinner />
      </div>
    );
  if (!me) return <OwnerLogin onIn={load} />;
  if (!me.mfaVerified) return <OwnerMfa me={me} onIn={load} onOut={out} />;
  return (
    <div className="owner">
      <header className="topbar">
        <div className="row gap-sm">
          <img src="/civilhound-icon-clear.webp" alt="" width={30} height={30} />
          <strong>CivilHound</strong> <span className="muted-on-dark">Owner console</span>
        </div>
        <div className="row gap">
          <span className="small muted-on-dark">{me.email}</span>
          <button className="btn sm ghost-on-dark" onClick={out}>
            Sign out
          </button>
        </div>
      </header>
      <main className="owner-main stack">
        <div className="tabs" role="tablist">
          {(
            [
              ['agencies', 'Agencies'],
              ['email', 'Email log'],
              ['activity', 'Activity'],
            ] as const
          ).map(([k, l]) => (
            <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'active' : ''} onClick={() => setTab(k)}>
              {l}
            </button>
          ))}
        </div>
        {tab === 'agencies' ? <Agencies me={me} /> : tab === 'email' ? <EmailLog me={me} /> : <Activity />}
      </main>
    </div>
  );
}
